import { useNavigationClient } from '@/api-proxy/useProxiedClient'
import {
  getAllVehiclesQueryKey,
  getCurrentDrivesQueryKey,
  setVehicleMaintenanceModeMutation,
  updateVehicleMutation,
} from '@/api/main'
import { VehicleResponse, VehicleUpdateRequestBody } from '@/api/main/types.gen'
import { getNavigationOptions, getNavigationQueryKey, putGoToGarageMutation } from '@/api/navigationApi'
import { extendKey } from '@/utils/queryKey'
import { TextInput, Button, Modal, Select, Stack, Textarea, Tabs, Switch } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconCar } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PiSliders } from 'react-icons/pi'
import { IoHardwareChipSharp } from 'react-icons/io5'

export const VehicleEditForm = ({
  modalOpen,
  onClose,
  vehicles,
}: {
  modalOpen: boolean
  onClose: () => void
  vehicles: VehicleResponse[]
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleResponse | null>(null)
  const [maintenanceMode, setMaintenanceMode] = useState(selectedVehicle?.in_maintenance_mode)
  const [goToGarage, setGoToGarage] = useState(false)

  const client = useNavigationClient(selectedVehicle?.id)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation(['vehicles', 'common'])
  const { mutate: updateVehicle, isPending } = useMutation({
    ...updateVehicleMutation(),
    onSuccess: () => {
      notifications.show({ message: t('vehicleUpdated') })
      queryClient.invalidateQueries({ queryKey: [getCurrentDrivesQueryKey(), getAllVehiclesQueryKey()] })
      onClose()
    },
    onError: () => {
      notifications.show({ message: t('errorUpdatingVehicle'), color: 'red' })
    },
  })

  const { mutate: updateMaintenanceMode } = useMutation({
    ...setVehicleMaintenanceModeMutation(),
    onSuccess: () => {
      notifications.show({ message: t('maintenanceModeUpdated') })
      queryClient.invalidateQueries({ queryKey: [getAllVehiclesQueryKey(), getCurrentDrivesQueryKey()] })
      onClose()
    },
    onError: () => {
      notifications.show({ message: t('errorUpdatingMaintenanceMode'), color: 'red' })
    },
  })

  const { data: navigationData, isLoading: isNavigationDataLoading } = useQuery({
    ...extendKey(getNavigationOptions({ client }), selectedVehicle?.id),
    staleTime: 2000,
    enabled: !!selectedVehicle && !!client,
  })

  const { mutate: updateGoToGarage } = useMutation({
    ...putGoToGarageMutation({ client }),
    onSuccess: () => {
      notifications.show({ message: t('goToGarageUpdated') })
      onClose()
    },
    onError: () => {
      notifications.show({ message: t('errorUpdatingGoToGarage'), color: 'red' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [getNavigationQueryKey({ client })] })
    },
  })

  const handleGoToGarage = (checked: boolean) => setGoToGarage(checked)

  const handleSaveMode = () => {
    if (goToGarage !== navigationData?.go_to_garage) {
      updateGoToGarage({ body: { go_to_garage: goToGarage } })
    }
    if (!!selectedVehicle?.id && maintenanceMode !== selectedVehicle.in_maintenance_mode) {
      updateMaintenanceMode({
        path: { vehicle_id: selectedVehicle!.id! },
        body: { in_maintenance_mode: maintenanceMode || false },
      })
    }
  }

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      password: '',
      phoneNumber: '',
      routerPhoneNumber: '',
      vpnAddress: '',
      modemVpnAddress: '',
      controlUnitVpnAddress: '',
      note: '',
    },
  })

  useEffect(() => {
    const vehicle = vehicles.find(v => v.id === selectedVehicle?.id)
    if (vehicle) {
      form.setValues({
        password: '',
        phoneNumber: vehicle.phone_number || '',
        routerPhoneNumber: vehicle.router_phone_number || '',
        vpnAddress: vehicle.vpn_address || '',
        modemVpnAddress: vehicle.modem_vpn_address || '',
        controlUnitVpnAddress: vehicle.control_unit_vpn_address || '',
        note: vehicle.note || '',
      })
      // Reset form dirty state
      form.resetDirty()
    }
    // form is a stable Mantine form ref; including it would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle, vehicles])

  // Reset goToGarage when vehicle changes
  useEffect(() => {
    if (selectedVehicle?.id) {
      // Reset to false first when vehicle changes
      setGoToGarage(false)
      setMaintenanceMode(false)
      // Then update when navigationData loads
      if (navigationData?.go_to_garage !== undefined) {
        setGoToGarage(navigationData.go_to_garage)
      }

      if (selectedVehicle?.in_maintenance_mode !== undefined) {
        setMaintenanceMode(selectedVehicle.in_maintenance_mode)
      }
    }
  }, [selectedVehicle?.id, selectedVehicle?.in_maintenance_mode, navigationData?.go_to_garage])

  return (
    <Modal opened={modalOpen} onClose={onClose} size="lg" title={t('editVehicle')}>
      <Select
        label={t('vehicle')}
        placeholder={t('selectVehicle')}
        data={vehicles.map(vehicle => ({ label: vehicle.id, value: vehicle.id }))}
        value={selectedVehicle?.id}
        onChange={value => setSelectedVehicle(vehicles.find(v => v.id === value) || null)}
        mb="md"
      />{' '}
      <Tabs defaultValue="details">
        <Tabs.List mb="md">
          <Tabs.Tab value="details" leftSection={<IconCar size={16} />}>
            {t('details')}
          </Tabs.Tab>
          <Tabs.Tab value="mode" leftSection={<PiSliders size={16} />} disabled={!selectedVehicle}>
            {t('mode')}
          </Tabs.Tab>
          <Tabs.Tab
            value="hardware"
            leftSection={<IoHardwareChipSharp size={16} />}
            onClick={() => {
              navigate({
                to: `/hardware/${selectedVehicle?.id}/restarts`,
              })
            }}
            disabled={!selectedVehicle}
          >
            {t('hardware')}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="mode">
          <Stack mb="md">
            <Switch
              size="sm"
              label={t('maintenanceMode')}
              disabled={!selectedVehicle}
              checked={maintenanceMode}
              onChange={event => setMaintenanceMode(event.target.checked)}
            />
            <Switch
              size="sm"
              label={t('goToGarage')}
              disabled={!selectedVehicle || isNavigationDataLoading}
              checked={goToGarage}
              onChange={event => handleGoToGarage(event.target.checked)}
            />
          </Stack>
          <Button
            styles={{ root: { alignSelf: 'start' } }}
            disabled={!selectedVehicle}
            onClick={handleSaveMode}
            loading={isPending}
          >
            {t('save', { ns: 'common' })}
          </Button>
        </Tabs.Panel>

        <Tabs.Panel value="details">
          <form
            onSubmit={form.onSubmit(values => {
              if (!selectedVehicle?.id) {
                return
              }

              const body: VehicleUpdateRequestBody = {
                password: values.password.trim() === '' ? undefined : values.password,
                phone_number: values.phoneNumber.trim() === '' ? undefined : values.phoneNumber,
                router_phone_number: values.routerPhoneNumber.trim() === '' ? undefined : values.routerPhoneNumber,
                vpn_address: values.vpnAddress.trim() === '' ? undefined : values.vpnAddress,
                modem_vpn_address: values.modemVpnAddress.trim() === '' ? undefined : values.modemVpnAddress,
                control_unit_vpn_address:
                  values.controlUnitVpnAddress.trim() === '' ? undefined : values.controlUnitVpnAddress,
                note: values.note.trim() === '' ? undefined : values.note,
              }

              updateVehicle({
                path: { vehicle_id: selectedVehicle.id },
                body,
              })
            })}
          >
            <Stack>
              <TextInput
                key={form.key('password')}
                label={t('password')}
                {...form.getInputProps('password')}
                type="password"
                disabled={!selectedVehicle}
              />
              <TextInput
                key={form.key('phoneNumber')}
                label={t('phoneNumber')}
                {...form.getInputProps('phoneNumber')}
                disabled={!selectedVehicle}
              />
              <TextInput
                key={form.key('routerPhoneNumber')}
                label={t('routerPhoneNumber')}
                {...form.getInputProps('routerPhoneNumber')}
                disabled={!selectedVehicle}
              />
              <TextInput
                key={form.key('vpnAddress')}
                label={t('carVpnIp')}
                {...form.getInputProps('vpnAddress')}
                disabled={!selectedVehicle}
              />
              <TextInput
                key={form.key('modemVpnAddress')}
                label={t('modemVpnIp')}
                {...form.getInputProps('modemVpnAddress')}
                disabled={!selectedVehicle}
              />
              <TextInput
                key={form.key('controlUnitVpnAddress')}
                label={t('controlUnitVpnIp')}
                {...form.getInputProps('controlUnitVpnAddress')}
                disabled={!selectedVehicle}
              />
              <Textarea
                key={form.key('note')}
                label={t('note')}
                {...form.getInputProps('note')}
                disabled={!selectedVehicle}
              />
              <Button
                type="submit"
                styles={{ root: { alignSelf: 'start' } }}
                loading={isPending}
                disabled={!selectedVehicle}
              >
                {t('save', { ns: 'common' })}
              </Button>
            </Stack>
          </form>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  )
}
